import { ChallengeService } from '@duck/api-definitions'
import {
  CheckCircleIcon,
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon,
} from '@heroicons/react/24/outline'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Button,
  cn,
  SegmentedToggle,
  CopyableId,
} from '@duck/ui'
import { useTar } from '@/modules'
import { useAtomValue } from 'jotai'
import { debugDevEnabledAtom } from '@/store/debugAtoms'
import { useEffect, useState, type ComponentType } from 'react'
import { SubmitUserFeedback } from './SubmitUserFeedback'
import { FeedbackContent, SolutionContent } from './content'
import { FeedbackRail } from './FeedbackRail'

type ViewMode = 'feedback' | 'solution' | 'submitFeedback'
type ResultsTab = 'feedback' | 'solution'

interface FeedbackOverviewProps {
  isOpen: boolean
  onClose: () => void
  challenge: CompletedChallenge
  onShareFeedback?: (challenge: CompletedChallenge) => void
  onFeedbackSubmitted?: (feedback: ChallengeService.UserFeedback) => void
  /** Fires when the user opts to publicly share this achievement. */
  onShareAchievement?: (challenge: CompletedChallenge) => void
  initialViewMode?: ViewMode
  /** When true, show employer feedback and grade instead of AI feedback */
  showEmployer?: boolean
}

export interface CompletedChallenge extends ChallengeService.ChallengeInfo {
  icon?: ComponentType<{ className?: string }>
  estimatedTime?: string
}

export const FeedbackDialog = ({
  isOpen,
  onClose,
  challenge,
  onShareFeedback,
  onFeedbackSubmitted,
  onShareAchievement,
  initialViewMode = 'feedback',
  showEmployer = false
}: FeedbackOverviewProps) => {
  const { t } = useTar()
  const debugDevEnabled = useAtomValue(debugDevEnabledAtom)
  const [viewMode, setViewMode] = useState<ViewMode>(initialViewMode)
  const [userFeedbackSubmitted, setUserFeedbackSubmitted] = useState<boolean>(!!challenge.challenge.userFeedback)
  const [isMaximized, setIsMaximized] = useState(false)

  useEffect(() => {
    if (isOpen) {
      setViewMode(initialViewMode)
      setIsMaximized(false)
    }
  }, [isOpen, initialViewMode])

  if (!challenge) {
    return null
  }

  const { task } = challenge

  const handleShareFeedbackClick = () => {
    onShareFeedback?.(challenge)
    setViewMode('submitFeedback')
  }

  const handleFeedbackSubmissionSuccess = (feedback: ChallengeService.UserFeedback) => {
    setUserFeedbackSubmitted(true)
    setViewMode('feedback')
    onFeedbackSubmitted?.(feedback)
  }

  const isSubmittingFeedback = viewMode === 'submitFeedback'
  const resultsTab: ResultsTab = viewMode === 'solution' ? 'solution' : 'feedback'
  const displayGrade = showEmployer
    ? challenge.challenge.employerFeedback?.grade
    : challenge.challenge.feedbackGrade
  // The backend nulls the grade unless it qualifies as a shareable achievement,
  // so a present grade is the single signal for both the score and the Share CTA.
  const isAchievement = displayGrade != null

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        width="md"
        className={cn(
          isMaximized &&
            'sm:inset-0 sm:translate-x-0 sm:translate-y-0 sm:max-w-none sm:h-screen sm:max-h-screen sm:rounded-none sm:border-0'
        )}
      >
        {!isSubmittingFeedback && (
          <button
            type="button"
            onClick={() => setIsMaximized((prev) => !prev)}
            aria-label={t(isMaximized ? 'common.minimize' : 'common.maximize')}
            className="absolute top-4 right-12 rtl:left-12 rtl:right-auto z-10 hidden rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:block"
          >
            {isMaximized ? (
              <ArrowsPointingInIcon className="h-5 w-5" />
            ) : (
              <ArrowsPointingOutIcon className="h-5 w-5" />
            )}
          </button>
        )}

        <DialogHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CheckCircleIcon className="w-8 h-8 text-success" />
              <div>
                <DialogTitle className="text-xl">
                  {isSubmittingFeedback
                    ? t('userFeedback.evaluateTheFeedback')
                    : t('challenges.resultsTitle')}
                </DialogTitle>
                <p className="text-sm text-muted-foreground">{task.name}</p>
              </div>
            </div>
            {isSubmittingFeedback && (
              <Button
                variant="outline"
                size="sm"
                className="me-4"
                onClick={() => setViewMode('feedback')}
              >
                {t('userFeedback.backToResults')}
              </Button>
            )}
          </div>
          {debugDevEnabled && (
            <div className="mt-2 flex items-center gap-3" dir="ltr">
              <CopyableId value={challenge.challenge.id} visibleChars={12} />
              <Button
                variant="outline"
                size="sm"
                onClick={() => console.log('[FeedbackDialog] challenge', challenge)}
              >
                Log challenge
              </Button>
            </div>
          )}
        </DialogHeader>

        {isSubmittingFeedback ? (
          <div className="overflow-y-auto custom-scrollbar pe-2 max-h-[calc(90vh-200px)]">
            <SubmitUserFeedback
              key={challenge.challenge.id}
              challengeId={challenge.challenge.id}
              onCancel={() => setViewMode('feedback')}
              onSuccess={handleFeedbackSubmissionSuccess}
            />
          </div>
        ) : (
          <div className="flex flex-col gap-6 md:min-h-0 md:flex-1 md:flex-row md:gap-8">
            <FeedbackRail
              challenge={challenge}
              showEmployer={showEmployer}
              canShare={!!onShareAchievement && !showEmployer && isAchievement}
              onShareAchievement={() => onShareAchievement?.(challenge)}
              canEvaluate={!showEmployer && !userFeedbackSubmitted}
              onEvaluate={handleShareFeedbackClick}
            />

            <div className="flex min-w-0 flex-1 flex-col md:min-h-0">
              <SegmentedToggle
                className="mb-4 shrink-0"
                value={resultsTab}
                onChange={(value) => setViewMode(value)}
                segments={[
                  { value: 'feedback', label: t('challenges.feedbackTab') },
                  { value: 'solution', label: t('challenges.solutionTab') },
                ]}
              />

              <div className="overflow-y-auto custom-scrollbar pe-1 md:min-h-0 md:flex-1">
                {resultsTab === 'solution' ? (
                  <SolutionContent
                    workState={challenge.challenge.workState}
                    className="h-full"
                  />
                ) : (
                  <FeedbackContent
                    challenge={challenge}
                    showEmployer={showEmployer}
                    showGrade={false}
                    showChallengeDetails={false}
                  />
                )}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
